import React, {
  useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import { Form } from 'antd';
import moment from 'moment';
import { TCM } from 'components/tcm';
import { CHAR } from 'settings/namedUnicode';
import { connect } from 'react-redux';
import { fetchList } from 'services/tcm/campaign';
import {
  tcmReportExport as tcmReportExportAction,
  tcmReportExportReset as tcmReportExportResetAction,
  saveReportTemplate as saveReportTemplateAction,
  saveReportTemplateReset as saveReportTemplateResetAction,
  getTcmReportSettings as getTcmReportSettingsAction,
  getTcmReportSettingsReset as getTcmReportSettingsResetAction,
} from 'redux/tcmReport/actions';
import {
  campaignGetReport as campaignGetReportAction,
  campaignResetReport as campaignResetReportAction,
} from 'redux/tcmCampaign/actions';

import { CreateErrorModal, CreateDraftSuccessfulModal } from 'components/tcm/Modals';

import { ModalWithChildren } from 'components/tcm/ClientInfo/style';
import { debounce } from 'underscore';
import { formatCampaignResponse } from 'tcm/campaigns/format';
import {
  ConversionBlock,
  ConversionCount, DescriptionBlockTitle, DescriptionText,
  DivisionCount,
  DivisionRow,
  DoubleFunnelItem,
  Equally,
  FunnelItem,
  Page,
  ReportDescription,
  ReportDescriptionBody,
  ReportDescriptionTitle,
  ModalText,
  FormItem,
  FiltersWrapper,
  PageTitle,
  BlockTitle,
  FormWrapper,
  FoundedCampaigns,
  DatePresets,
  DescriptionItem,
  ItemTitle,
  ItemContent,
  FunnelWrapper,
  FunnelTitle,
  Funnel,
  Header,
  ButtonsWrapper,
  NoResults,
} from './style';

import ReportList from './RerpotList';

import { formFields, DateRangePresets } from './data';

const initialValues = {
  [formFields.dateRange.key]: 'allTime',
  [formFields.dateFrom.key]: undefined,
  [formFields.dateTo.key]: moment(),
};

const searchCampaign = debounce(fetchList, 300, true);

const halfAYearDate = moment().quarter() >= 3
  ? moment().startOf('year').add(6, 'M')
  : moment().startOf('year');

const checkValidCampaigns = (campaigns = []) => Array.isArray(campaigns) && campaigns.length > 0;

const CampaignsReport = ({
  // report
  reportData,
  reportIsLoading,
  reportIsLoaded,
  // reportError,

  // redux
  exportReportData,
  exportReportIsLoading,
  exportReportIsLoaded,
  exportReportError,

  // reportTemplateData,
  reportTemplateIsLoading,
  reportTemplateIsLoaded,
  reportTemplateError,

  reportSettingsData,
  // reportSettingsIsLoading,
  reportSettingsIsLoaded,
  // reportSettingsError,

  // actions
  tcmReportExport,
  tcmReportExportReset,

  saveReportTemplate,
  saveReportTemplateReset,

  campaignGetReport,
  campaignResetReport,

  getTcmReportSettings,
  getTcmReportSettingsReset,
}) => {
  const [templateName, setTemplateName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [query, setQuery] = useState('');
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [requestReportData, setRequestReportData] = useState(null);
  const [showReportsForm, setReportsShowForm] = useState(true);

  const searchResultsElement = useRef(null);

  const [form] = Form.useForm();

  const { funnelSaleList, conversionList } = reportData;

  const campaignList = formatCampaignResponse(reportData).map((campaign) => ({
    campaignId: campaign.id,
    label: `${campaign.brand} ${campaign.model} (${campaign.dialingFrom} - ${campaign.dialingTo})`,
  }));

  const totalCount = funnelSaleList ? funnelSaleList[0]?.items[0].count : null;

  const firstDialingDate = useMemo(() => (
    moment(reportSettingsData.firstDialingDate).isValid()
      ? moment(reportSettingsData.firstDialingDate)
      : undefined
  ), [reportSettingsData]);

  const funnel = useMemo(() => {
    if (funnelSaleList) {
      return (
        funnelSaleList.map((step, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            {step.items.length === 1 && (
              <FunnelItem
                total={totalCount}
                value={step.items[0].count}
                text={step.items[0].label}
                isFinal={step.level === 8}
                withoutText={[6, 7, 8].includes(step.level)}
              />
            )}

            {step.items.length === 2 && (
              <DoubleFunnelItem
                total={totalCount}
                firstValue={step.items[0].count}
                firstText={step.items[0].label}
                secondaryText={step.items[1].label}
                secondaryValue={step.items[1].count}
              />
            )}
          </li>
        ))
      );
    }

    return [];
  }, [funnelSaleList, totalCount]);

  const clearSearch = (event) => {
    if (event && !event.path.some((el) => el === searchResultsElement.current)) {
      setSearchResult([]);
      setQuery('');
    }
  };

  const onSubmit = (values) => {
    const {
      dateFrom,
      dateTo,
      campaigns,
    } = values;

    const data = {
      startAt: dateFrom.format('YYYY-MM-DD'),
      finishAt: dateTo.format('YYYY-MM-DD'),
      campaignIds: campaigns,
    };
    setRequestReportData(data);
    campaignGetReport(data);
    setReportsShowForm(false);
    clearSearch();
  };

  const onReset = () => {
    form.setFieldsValue({
      [formFields.dateFrom.key]: undefined,
      [formFields.dateTo.key]: undefined,
      [formFields.dateRange.key]: '',
      [formFields.campaigns.key]: undefined,
    });
  };

  const onDateRangeChange = (event) => {
    switch (event.target.value) {
      case DateRangePresets.month.value: {
        form.setFieldsValue({
          [formFields.dateFrom.key]: moment().startOf('month'),
          [formFields.dateTo.key]: moment(),
        });
        break;
      }
      case DateRangePresets.quarter.value: {
        form.setFieldsValue({
          [formFields.dateFrom.key]: moment().startOf('quarter'),
          [formFields.dateTo.key]: moment(),
        });
        break;
      }
      case DateRangePresets.halfAYear.value: {
        form.setFieldsValue({
          [formFields.dateFrom.key]: halfAYearDate,
          [formFields.dateTo.key]: moment(),
        });
        break;
      }
      case DateRangePresets.year.value: {
        form.setFieldsValue({
          [formFields.dateFrom.key]: moment().startOf('year'),
          [formFields.dateTo.key]: moment(),
        });
        break;
      }
      case DateRangePresets.allTime.value: {
        form.setFieldsValue({
          [formFields.dateFrom.key]: firstDialingDate,
          [formFields.dateTo.key]: moment(),
        });
        break;
      }

      default:
        console.error('onDateRangeChange: Incorrect value of date range: ', event.target.value);
    }
  };

  const clearCurrentDateRangePresets = () => {
    form.setFieldsValue({
      [formFields.dateRange.key]: '',
    });
  };

  const getCampaigns = useCallback(
    (event) => {
      setQuery(event.target.value);
      setIsResultsLoading(true);
      searchCampaign({ search: event.target.value }).then((resp) => {
        const searchResult = formatCampaignResponse(resp.data).map((campaign) => ({
          value: campaign.id,
          label: `${campaign.brand} ${campaign.model} (${campaign.dialingFrom} - ${campaign.dialingTo})`,
        }));
        setSearchResult(searchResult);
        setIsResultsLoading(false);
      });

      form.setFields([
        {
          name: formFields.campaigns.key,
          value: [],
        },
      ]);
    },
    [form],
  );

  const onSearchChange = (event) => {
    getCampaigns(event);
  };

  const onSearchAll = (event) => {
    if (event?.target?.value === '' && !searchResult.length) {
      getCampaigns(event);
    }
  };

  const handleGetReportData = (reportData) => {
    const {
      dateFrom, dateTo, campaigns = [], search,
    } = reportData || {};
    const event = { target: { value: search } };
    onSearchChange(event);
    form.setFields([
      {
        name: formFields.dateFrom.key,
        value: dateFrom,
      },
      {
        name: formFields.dateTo.key,
        value: dateTo,
      },
      {
        name: formFields.campaigns.key,
        value: campaigns,
      },
    ]);
  };

  const conversionData = conversionList?.[0] || {};
  const conversionDividend = conversionData.params?.[0].value;
  const conversionDivider = conversionData.params?.[1].value;
  const { conversion } = conversionData;

  useEffect(() => {
    if (exportReportIsLoaded) {
      const url = URL.createObjectURL(exportReportData);
      const shadowLink = document.createElement('a');
      shadowLink.href = url;
      shadowLink.download = 'Report.xlsx';
      shadowLink.click();
      tcmReportExportReset();
    }
  }, [exportReportData, exportReportIsLoaded, tcmReportExportReset]);

  useEffect(() => {
    if (reportTemplateIsLoaded) {
      setShowModal(false);
    }
  }, [reportTemplateIsLoaded]);

  useEffect(() => {
    if (!reportSettingsIsLoaded) {
      getTcmReportSettings();
    } else {
      form.setFieldsValue({
        [formFields.dateFrom.key]: firstDialingDate,
      });
    }
  }, [
    reportSettingsIsLoaded,
    form,
    reportSettingsData,
    getTcmReportSettings,
    firstDialingDate,
  ]);

  useEffect(() => () => {
    campaignResetReport();
    getTcmReportSettingsReset();
  }, [campaignResetReport, getTcmReportSettingsReset]);

  /* May need */

  // useEffect(() => {
  //   window.addEventListener('click', clearSearch);
  //
  //   return () => window.removeEventListener('click', clearSearch);
  // }, []);

  if (reportIsLoading) {
    return (
      <TCM.Loader text="Идет построение отчета..." />
    );
  }

  return (
    <Page>
      <Header>
        <PageTitle>Отчет</PageTitle>
        {requestReportData && !showReportsForm && (
          <TCM.ButtonOutline
            widthSize="fixed"
            onClick={() => tcmReportExport(requestReportData)}
            loading={exportReportIsLoading}
          >
            Выгрузить в Excel
          </TCM.ButtonOutline>
        )}
      </Header>

      <FiltersWrapper>
        <BlockTitle>По кампаниям</BlockTitle>
        <ReportList show={showReportsForm} getReportData={handleGetReportData} />
        <FormWrapper show={showReportsForm}>
          <Form
            form={form}
            onFinish={onSubmit}
            layout="vertical"
            name="clientFilter"
            initialValues={initialValues}
            validateTrigger="onSubmit"
          >

            <TCM.DateRange
              propsLabelFrom={{
                name: formFields.dateFrom.key,
                label: formFields.dateFrom.label,
                validateTrigger: 'onBlur',
                rules: formFields.dateFrom.rules,
              }}
              propsInputFrom={{
                onChange: clearCurrentDateRangePresets,
                name: formFields.dateFrom.key,
              }}
              propsLabelTo={{
                name: formFields.dateTo.key,
                label: formFields.dateTo.label,
                validateTrigger: 'onBlur',
                rules: formFields.dateTo.rules,
              }}
              propsInputTo={{
                name: formFields.dateTo.key,
                onChange: clearCurrentDateRangePresets,
              }}
            />

            <DatePresets>
              <TCM.Label noStyle name={formFields.dateRange.key}>
                <TCM.RadioTextGroup onChange={onDateRangeChange}>
                  <TCM.RadioBtnText value={DateRangePresets.allTime.value}>
                    {DateRangePresets.allTime.label}
                  </TCM.RadioBtnText>
                  <TCM.RadioBtnText value={DateRangePresets.year.value}>
                    {DateRangePresets.year.label}
                  </TCM.RadioBtnText>
                  <TCM.RadioBtnText value={DateRangePresets.halfAYear.value}>
                    {DateRangePresets.halfAYear.label}
                  </TCM.RadioBtnText>
                  <TCM.RadioBtnText value={DateRangePresets.quarter.value}>
                    {DateRangePresets.quarter.label}
                  </TCM.RadioBtnText>
                  <TCM.RadioBtnText value={DateRangePresets.month.value}>
                    {DateRangePresets.month.label}
                  </TCM.RadioBtnText>
                </TCM.RadioTextGroup>
              </TCM.Label>
            </DatePresets>

            <TCM.Label
              label="Кампания"
            >
              <TCM.InputSearch
                placeholder="Поиск"
                value={query}
                onChange={onSearchChange}
                onFocus={onSearchAll}
                loading={isResultsLoading}
              />
            </TCM.Label>

            {Boolean(searchResult.length) && (
              <FoundedCampaigns ref={searchResultsElement}>
                <TCM.Label
                  noStyle
                  name={formFields.campaigns.key}
                >
                  <TCM.CheckBoxGroup
                    options={searchResult}
                  />
                </TCM.Label>
              </FoundedCampaigns>
            )}

            <ButtonsWrapper>
              <TCM.ButtonGroup flexPosition="flex-start">

                <Form.Item shouldUpdate noStyle>
                  {() => (
                    <TCM.Button
                      htmlType="submit"
                      loading={reportIsLoading}
                      disabled={!(
                        form.getFieldValue(formFields.dateFrom.key)
                          && form.getFieldValue(formFields.dateTo.key)
                          && checkValidCampaigns(form.getFieldValue(formFields.campaigns.key))
                      ) || (isResultsLoading)}
                    >
                      Построить отчет
                    </TCM.Button>
                  )}
                </Form.Item>

                <Form.Item noStyle>
                  <TCM.ButtonOutline onClick={onReset}>
                    Сбросить фильтры
                  </TCM.ButtonOutline>
                </Form.Item>
              </TCM.ButtonGroup>
            </ButtonsWrapper>
          </Form>
        </FormWrapper>

        {!showReportsForm && (
          <div>
            <DescriptionItem>
              <ItemTitle>Действие кампании:</ItemTitle>
              <ItemContent>
                {moment(requestReportData.startAt).format('DD.MM')}
                {' — '}
                {moment(requestReportData.finishAt).format('DD.MM.YYYY')}
              </ItemContent>
            </DescriptionItem>

            <DescriptionItem>
              <ItemTitle>Кампания:</ItemTitle>
              <div>
                {Boolean(campaignList.length) && reportIsLoaded
                 && campaignList.map(({ label, campaignId }) =>
                   <ItemContent key={campaignId}>{label}</ItemContent>)}
              </div>
            </DescriptionItem>

            <TCM.ButtonGroup flexPosition="flex-start">
              <TCM.Button onClick={() => setReportsShowForm(true)}>Изменить</TCM.Button>
              <TCM.ButtonOutline
                onClick={() => setShowModal(true)}
              >
                Сохранить шаблон
              </TCM.ButtonOutline>
            </TCM.ButtonGroup>
          </div>
        )}
      </FiltersWrapper>

      {
        reportIsLoaded && !showReportsForm && (
          funnel.length > 0 ? (
            <>
              <FunnelWrapper>
                <FunnelTitle>Воронка продаж</FunnelTitle>

                <Funnel>
                  {funnel}
                </Funnel>

                {Boolean(conversion) && (
                  <ConversionBlock>
                    <ConversionCount>
                      Конверсия
                      {CHAR.NBSP}
                      {`${Math.round(conversion * 100) / 100} %`}
                    </ConversionCount>
                    <Equally />
                    <div>
                      <DivisionRow>
                        <DivisionCount>{conversionDividend}</DivisionCount>
                        {CHAR.NBSP}
                        Клиентов взяли кредит
                      </DivisionRow>
                      <DivisionRow>
                        <DivisionCount>{conversionDivider}</DivisionCount>
                        {CHAR.NBSP}
                        Согласованных результатов звонка
                      </DivisionRow>
                    </div>
                  </ConversionBlock>
                )}

              </FunnelWrapper>

              <ReportDescription>
                <ReportDescriptionTitle>В воронку продаж включены:</ReportDescriptionTitle>
                <ReportDescriptionBody>
                  <div>
                    <DescriptionBlockTitle>Кампании:</DescriptionBlockTitle>
                    <div>
                      {formatCampaignResponse(reportData)?.map((campaign) => (
                        <DescriptionText>
                          {`${campaign.brand} ${campaign.model} (${campaign.dialingFrom} - ${campaign.dialingTo})`}
                        </DescriptionText>
                      ))}
                    </div>
                  </div>

                  <div>
                    <DescriptionBlockTitle>Сотрудники:</DescriptionBlockTitle>
                    <div>
                      {reportData.userList
                      && Array.isArray(reportData.userList)
                      && (reportData.userList.length > 0)
                        ? reportData.userList.map((item) => (
                          <DescriptionText>
                            {item.name}
                          </DescriptionText>
                        )) : (
                          <DescriptionText>&mdash;</DescriptionText>
                        )}
                    </div>
                  </div>
                </ReportDescriptionBody>
              </ReportDescription>
            </>
          )
            : (
              <NoResults>
                Информация не найдена. Попробуйте изменить данные для построения отчета
              </NoResults>
            )
        )

      }

      <ModalWithChildren
        visible={showModal}
        onOk={() => saveReportTemplate({
          reportType: 'campaign',
          name: templateName,
          params: { ...requestReportData, query },
        })}
        onClose={() => {
          setShowModal(false);
          setTemplateName('');
          saveReportTemplateReset();
        }}
        onCancel={
          () => {
            setShowModal(false);
            setTemplateName('');
            saveReportTemplateReset();
          }
        }
        title="Шаблон"
        btnTextOk="Сохранить"
        disabledOk={!templateName}
        onOkLoading={reportTemplateIsLoading}
        onOkError={reportTemplateError}
        btnTextCancel="Не сохранять"
      >
        <ModalText>
          Шаблон упрощает работу с отчетами.
          <br />
          Все параметры отчета будут сохранены, и вам не придется заново их выбирать.
        </ModalText>

        <FormItem label="Название шаблона">
          <TCM.Input
            placeholder="Укажите название"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
        </FormItem>

      </ModalWithChildren>

      <CreateErrorModal
        onCancel={() => {
          tcmReportExportReset();
        }}
        visible={Boolean(exportReportError)}
        title="Ошибка"
        description="Ошибка сервера выгрузка excel-файла невозможна, попробуйте позже"
      />

      <CreateDraftSuccessfulModal
        visible={reportTemplateIsLoaded}
        onCancel={() => {
          saveReportTemplateReset();
        }}
        title="Шаблон успешно сохранен"
      />
    </Page>
  );
};

export default connect(
  (state) => ({
    exportReportData: state.tcmReport.export.data,
    exportReportIsLoading: state.tcmReport.export.isLoading,
    exportReportIsLoaded: state.tcmReport.export.isLoaded,
    exportReportError: state.tcmReport.export.error,

    reportTemplateData: state.tcmReport.reportTemplate.data,
    reportTemplateIsLoading: state.tcmReport.reportTemplate.isLoading,
    reportTemplateIsLoaded: state.tcmReport.reportTemplate.isLoaded,
    reportTemplateError: state.tcmReport.reportTemplate.error,

    reportSettingsData: state.tcmReport.settings.data,
    reportSettingsIsLoading: state.tcmReport.settings.isLoading,
    reportSettingsIsLoaded: state.tcmReport.settings.isLoaded,
    reportSettingsError: state.tcmReport.settings.error,

    reportData: state.tcmCampaign.report.data,
    reportIsLoading: state.tcmCampaign.report.isLoading,
    reportIsLoaded: state.tcmCampaign.report.isLoaded,
    reportError: state.tcmCampaign.report.error,
  }),
  {
    tcmReportExport: tcmReportExportAction,
    tcmReportExportReset: tcmReportExportResetAction,
    saveReportTemplate: saveReportTemplateAction,
    saveReportTemplateReset: saveReportTemplateResetAction,
    getTcmReportSettings: getTcmReportSettingsAction,
    getTcmReportSettingsReset: getTcmReportSettingsResetAction,

    campaignGetReport: campaignGetReportAction,
    campaignResetReport: campaignResetReportAction,
  },
)(CampaignsReport);
